import { useState, useEffect, useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

/**
 * Date of Birth Picker with 3 dropdowns
 * - Month, Day, Year
 * - Handles leap years
 * - Day dropdown adjusts based on month/year
 */

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

// Check if a year is a leap year
const isLeapYear = (year) => {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
};

// Get days in a month
const getDaysInMonth = (month, year) => {
  const daysInMonths = {
    '01': 31, '02': isLeapYear(year) ? 29 : 28, '03': 31, '04': 30,
    '05': 31, '06': 30, '07': 31, '08': 31,
    '09': 30, '10': 31, '11': 30, '12': 31,
  };
  return daysInMonths[month] || 31;
};

const DateOfBirthPicker = ({ 
  value, 
  onChange, 
  onValidationChange,  // Callback to report validation status
  required = false,
  className = '',
  labelClassName = '',
  showLabel = true,
  minAge = 13,  // Minimum age requirement
  maxAge = 100, // Maximum age (for dropdown range)
}) => {
  // Parse existing value (YYYY-MM-DD format)
  const parseValue = (val) => {
    if (!val) return { year: '', month: '', day: '' };
    const parts = val.split('-');
    if (parts.length !== 3) return { year: '', month: '', day: '' };
    return {
      year: parts[0],
      month: parts[1],
      day: parts[2],
    };
  };

  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [validationError, setValidationError] = useState('');

  // Calculate exact age from full date of birth
  const calculateExactAge = (dob) => {
    const today = new Date();
    const birthDate = new Date(dob + 'T00:00:00');
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const dayDiff = today.getDate() - birthDate.getDate();
    
    // If birthday hasn't occurred yet this year, subtract 1
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }
    
    return age;
  };

  // Validate age using full date (year, month, day)
  const validateAge = (dateStr) => {
    if (!dateStr || dateStr.split('-').length !== 3) {
      setValidationError('');
      if (onValidationChange) onValidationChange({ isValid: false, error: '' });
      return false;
    }
    
    const age = calculateExactAge(dateStr);
    
    if (age < minAge) {
      const error = `You must be at least ${minAge} years old to create an account.`;
      setValidationError(error);
      if (onValidationChange) onValidationChange({ isValid: false, error });
      return false;
    }
    
    setValidationError('');
    if (onValidationChange) onValidationChange({ isValid: true, error: '' });
    return true;
  };

  // Initialize from value
  useEffect(() => {
    const parsed = parseValue(value);
    setSelectedYear(parsed.year);
    setSelectedMonth(parsed.month);
    setSelectedDay(parsed.day);
    if (value) {
      validateAge(value);
    }
  }, [value]);

  // Generate year options (from minAge to maxAge years ago)
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - maxAge;
    const endYear = currentYear - minAge;
    const yearList = [];
    for (let y = endYear; y >= startYear; y--) {
      yearList.push(y.toString());
    }
    return yearList;
  }, [minAge, maxAge]);

  // Generate day options based on selected month/year
  const days = useMemo(() => {
    const year = parseInt(selectedYear) || new Date().getFullYear();
    const daysCount = getDaysInMonth(selectedMonth || '01', year);
    return Array.from({ length: daysCount }, (_, i) => 
      (i + 1).toString().padStart(2, '0')
    );
  }, [selectedMonth, selectedYear]);

  // Update parent when any value changes
  const handleChange = (type, val) => {
    let newYear = selectedYear;
    let newMonth = selectedMonth;
    let newDay = selectedDay;

    if (type === 'year') {
      newYear = val;
      setSelectedYear(val);
    } else if (type === 'month') {
      newMonth = val;
      setSelectedMonth(val);
    } else if (type === 'day') {
      newDay = val;
      setSelectedDay(val);
    }

    // Adjust day if it exceeds the new month's days
    if (newMonth && newYear) {
      const maxDays = getDaysInMonth(newMonth, parseInt(newYear));
      if (parseInt(newDay) > maxDays) {
        newDay = maxDays.toString().padStart(2, '0');
        setSelectedDay(newDay);
      }
    }

    // Only call onChange when we have all three values
    if (newYear && newMonth && newDay) {
      const dateStr = `${newYear}-${newMonth}-${newDay}`;
      onChange(dateStr);
      validateAge(dateStr);
    } else if (!newYear && !newMonth && !newDay) {
      onChange('');
      setValidationError('');
      if (onValidationChange) onValidationChange({ isValid: false, error: '' });
    }
  };

  return (
    <div className={className}>
      {showLabel && (
        <Label className={labelClassName || "text-[#0B0B0B] font-medium"}>
          Date of Birth {required && '*'}
        </Label>
      )}
      <div className="grid grid-cols-3 gap-2 mt-1.5">
        {/* Month */}
        <Select
          value={selectedMonth}
          onValueChange={(val) => handleChange('month', val)}
        >
          <SelectTrigger 
            className="h-12 border-[#E5E7EB] focus:border-[#FF6A00] focus:ring-[#FF6A00]/20"
            data-testid="dob-month"
          >
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map(m => (
              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Day */}
        <Select
          value={selectedDay}
          onValueChange={(val) => handleChange('day', val)}
        >
          <SelectTrigger 
            className="h-12 border-[#E5E7EB] focus:border-[#FF6A00] focus:ring-[#FF6A00]/20"
            data-testid="dob-day"
          >
            <SelectValue placeholder="Day" />
          </SelectTrigger>
          <SelectContent>
            {days.map(d => (
              <SelectItem key={d} value={d}>{parseInt(d)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Year */}
        <Select
          value={selectedYear}
          onValueChange={(val) => handleChange('year', val)}
        >
          <SelectTrigger 
            className="h-12 border-[#E5E7EB] focus:border-[#FF6A00] focus:ring-[#FF6A00]/20"
            data-testid="dob-year"
          >
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            {years.map(y => (
              <SelectItem key={y} value={y}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Validation error message */}
      {validationError && (
        <p className="text-red-500 text-xs mt-2" data-testid="dob-error">
          {validationError}
        </p>
      )}
    </div>
  );
};

export default DateOfBirthPicker;
